import type { ComponentProps, ReactNode } from 'react';

import { DocuList } from '@/components/docu-list';
import { SidebarToggle } from '@/components/sidebar-toggle';
import { NoData } from '@flintmint/web-shared/components/no-data';
import { Icon } from '@flintmint/web-shared/components/icon';

import { useWorkspaceNoData } from '@/hooks/use-workspace-no-data';
import { useSidebarSearchAndFilters } from '@/hooks/use-sidebar-search-and-filters';

import type { SearchQuery } from '@/hooks/use-search';
import type { NoDataProps } from '@flintmint/web-shared/components/no-data';

export const WorkspaceSidebarList = (props: WorkspaceSidebarListProps) => {
  const {
    searchParamKey,
    filterParamKey,
    defaultFilterValue,
    getSearchableTypes,
    getNoDataConfig,
    searchQuery,
    renderFilters,
    renderActions,
    docuListProps,
  } = props;

  const {
    searchValue,
    filterValue,
    clearSearch,
    resetFilters,
    setSearchValue,
    setFilterValue,
    appliedFilters,
    setAppliedFilters,
    hasDateFilterSelection,
  } = useSidebarSearchAndFilters({
    searchParamKey,
    filterParamKey,
    defaultFilterValue,
  });

  const sidebarHasFilters = !!filterParamKey && !!filterValue && filterValue !== defaultFilterValue;
  const hasTagFilters = !!(appliedFilters.includedTagIds.length || appliedFilters.excludedTagIds.length);
  const hasFilters = sidebarHasFilters || hasTagFilters || hasDateFilterSelection;

  const noDataConfig = getNoDataConfig({
    hasSearchValue: !!searchValue,
    hasFilters,
    filterValue,
    clearSearch,
    resetFilters,
  });

  const noDataProps = useWorkspaceNoData({
    icon: noDataConfig.icon,
    descriptions: noDataConfig.descriptions,
    clearSearch,
    resetFilters,
    hasFilters,
    hasSearchValue: !!searchValue,
    defaultActions: noDataConfig.defaultActions,
    defaultActionRenderer: noDataConfig.defaultActionRenderer,
  });

  const searchableTypes = getSearchableTypes({ filterValue });

  return (
    <DocuList
      {...docuListProps}
      appliedFilters={appliedFilters}
      onAppliedFiltersChange={setAppliedFilters}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      searchQuery={searchQuery}
      searchableTypes={searchableTypes}
      filters={renderFilters?.({ filterValue, setFilterValue })}
      actions={
        <>
          {renderActions()}
          <SidebarToggle />
        </>
      }
      noData={
        <NoData
          color="darker"
          data-loaded="true"
          description={noDataProps.description}
          actionRenderer={noDataProps.actionRenderer}
          icon={<Icon size={48} multiColor type={noDataProps.iconType} />}
        />
      }
    />
  );
};

type DocuListProps = ComponentProps<typeof DocuList>;

type NoDataConfig = {
  icon: string;
  descriptions: Record<string, string>;
  defaultActions?: NoDataProps['actions'];
  defaultActionRenderer?: NoDataProps['actionRenderer'];
};

interface WorkspaceSidebarListProps {
  searchParamKey: string;
  filterParamKey?: string;
  defaultFilterValue?: string;
  getSearchableTypes: (params: { filterValue?: string }) => string[];
  getNoDataConfig: (params: {
    hasSearchValue: boolean;
    hasFilters: boolean;
    filterValue?: string;
    clearSearch: () => void;
    resetFilters?: () => void;
  }) => NoDataConfig;
  searchQuery?: Partial<SearchQuery>;
  renderFilters?: (params: { filterValue?: string; setFilterValue?: (value: string) => void }) => ReactNode;
  renderActions: () => ReactNode;
  docuListProps: Omit<
    DocuListProps,
    'searchValue' | 'onSearchChange' | 'noData' | 'filters' | 'actions' | 'appliedFilters' | 'onAppliedFiltersChange'
  >;
}
